import type { RouteLocationNormalized } from 'vue-router'

export interface RouteGroup {
  name: string
  routes: string[]
  parentKey: string
}

export const ROUTE_GROUPS: RouteGroup[] = [
  {
    name: 'agent-management',
    routes: ['dashboard', 'folder', 'chat', 'add-agent', 'agent-configure', 'monitor'],
    parentKey: 'dashboard',
  },
  {
    name: 'agent-store',
    routes: ['agent-store', 'ViewAgent', 'ViewEducationalResource', 'UploadAgent'],
    parentKey: 'agent-store',
  },
  {
    name: 'image-generation',
    routes: ['image-generation'],
    parentKey: 'image-generation',
  },
  {
    name: 'knowledge',
    routes: ['knowledge', 'knowledge.create', 'knowledge.detail', 'documents.create', 'documents.detail', 'documents.settings', 'knowledge.extraction'],
    parentKey: 'knowledge',
  },
  {
    name: 'tasks',
    routes: ['tasks', 'task-assign-agent'],
    parentKey: 'tasks',
  },
  {
    name: 'elearning',
    routes: ['elearning'],
    parentKey: 'elearning',
  },
  {
    name: 'slide',
    routes: ['slide'],
    parentKey: 'slide',
  },
  {
    name: 'brand-voices',
    routes: ['brand-voices'],
    parentKey: 'brand-voices',
  },
  {
    name: 'mind-map',
    routes: ['mind-map'],
    parentKey: 'mind-map',
  },
  {
    name: 'ai-translator',
    routes: ['ai-translator'],
    parentKey: 'ai-translator',
  },
  {
    name: 'image-translator',
    routes: ['image-translator'],
    parentKey: 'image-translator',
  },
  {
    name: 'video-summarizer',
    routes: ['video-summarizer'],
    parentKey: 'video-summarizer',
  },
  {
    name: 'deep-research',
    routes: ['deep-research'],
    parentKey: 'deep-research',
  },
  {
    name: 'image-to-word',
    routes: ['image-to-word'],
    parentKey: 'image-to-word',
  },
  {
    name: 'pdf-to-word',
    routes: ['pdf-to-word'],
    parentKey: 'pdf-to-word',
  },
  {
    name: 'settings',
    routes: ['workspace-settings', 'parameter-configuration'],
    parentKey: 'workspace-settings',
  },
  {
    name: 'transaction',
    routes: ['transaction'],
    parentKey: 'transaction',
  },
  //   {
  //     name: 'help-community',
  //     routes: ['user-guide', 'forum', 'contact-support'],
  //     parentKey: 'help-community',
  //   },
  {
    name: 'user',
    routes: ['user-profile'],
    parentKey: 'user-profile',
  },
  {
    name: 'classroom-student-detail',
    routes: [],
    parentKey: 'classroom.student.detail',
  },
  {
    name: 'classroom-teacher-detail',
    routes: [],
    parentKey: 'classroom.teacher.detail',
  },
  {
    name: 'classroom-teacher-subject',
    routes: [],
    parentKey: 'classroom.teacher.subject',
  },
  {
    name: 'classroom-teacher-member',
    routes: [],
    parentKey: 'classroom.teacher.member',
  },
  {
    name: 'classroom-teacher-result',
    routes: [],
    parentKey: 'classroom.teacher.result',
  },
  {
    name: 'classroom-teacher-exam-paper',
    routes: [],
    parentKey: 'classroom.teacher.exam-paper',
  },
  {
    name: 'classroom-teacher-question-bank',
    routes: [],
    parentKey: 'classroom.teacher.question-bank',
  },
  {
    name: 'classroom-teacher-agent',
    routes: [],
    parentKey: 'classroom.teacher.agent',
  },
]

/**
 * Get the active menu key for a given route
 */
export function getActiveMenuKey(route: RouteLocationNormalized): string | null {
  // Handle folder routes specially
  if (route.name === 'folder' && route.params.folderId)
    return `folder-${route.params.folderId}`

  // Check route group from meta
  if (route.meta?.group) {
    const group = ROUTE_GROUPS.find(g => g.name === route.meta?.group)
    if (group)
      return group.parentKey
  }

  // Fallback to route name
  const routeName = route.name
  return typeof routeName === 'string' ? routeName : null
}

export function getActiveMenuKeyClassroom(route: RouteLocationNormalized, isAdmin = false, keys: any[] = []): string | null {
  let routeName: any = route.name
  // Check route group from meta
  if (route.meta?.group) {
    const group = ROUTE_GROUPS.find(g => g.name === route.meta?.group)
    if (group)
      routeName = group.parentKey
  }

  if ((route.name === 'classroom.teacher.detail' || routeName === 'classroom.teacher.detail') && route.params.classroomId)
    routeName = isAdmin ? 'classroom-manage' : `classroom-${route.params.classroomId}`
  if ((route.name === 'classroom.student.detail' || routeName === 'classroom.student.detail') && route.params.subjectId) {
    if (keys?.length && keys.includes(`subject-${route.params.subjectId}`))
      routeName = `subject-${route.params.subjectId}`

    else
      routeName = 'classroom.student.detail'
  }
  // Fallback to route name

  return typeof routeName === 'string' ? routeName : null
}
/**
 * Get expanded menu keys based on current route
 */
export function getExpandedMenuKeys(route: RouteLocationNormalized, folders: any[] = []): string[] {
  const keys = ['workspace']

  // Always expand workspace to show folders
  folders.forEach((folder: any) => {
    keys.push(`folder-${folder.id}`)
  })

  // Expand parent menus based on current route group
  if (route.meta?.group === 'help-community')
    keys.push('help-community')

  return keys
}

export function getExpandedMenuKeysTeacher(route: RouteLocationNormalized, folders: any[] = []): string[] {
  const keys = ['classroom-manage']

  folders.forEach((folder: any) => {
    keys.push(`classroom-${folder.id}`)
  })

  return keys
}

export function getExpandedMenuKeysStudent(route: RouteLocationNormalized, folders: any[] = []): string[] {
  const keys: any[] = ['classroom.student.detail']

  folders.forEach((folder: any) => {
    keys.push(`subject-${folder.id}`)
  })

  return keys
}

/**
 * Check if a route belongs to a specific group
 */
export function isRouteInGroup(routeName: string, groupName: string): boolean {
  const group = ROUTE_GROUPS.find(g => g.name === groupName)
  return group ? group.routes.includes(routeName) : false
}

/**
 * Get all routes in a specific group
 */
export function getRoutesInGroup(groupName: string): string[] {
  const group = ROUTE_GROUPS.find(g => g.name === groupName)
  return group ? group.routes : []
}
